import React, { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import useOffers from "../../../features/adminDashboard/offers/useOffers";
import Loading from "../../../utils/LoadingIndicator/Loading";
import Dashboard from "../Dashboard";
import { API } from "../../../config";
import OfferForm from "../../../features/adminDashboard/offers/OfferForm";
import OfferTable from "../../../features/adminDashboard/offers/OfferTable";
import DeleteOfferModal from "../../../features/adminDashboard/offers/DeleteOfferModal";

const Offers = () => {
  const [activeTab, setActiveTab] = useState("view");
  const [editingOffer, setEditingOffer] = useState(null);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    offer: null,
  });

  // Use existing hooks
  const { offers, isGettingOffers, offersError, deleteOffer, isDeletingOffer } =
    useOffers();

  const handleEdit = (offer) => {
    setEditingOffer(offer);
    setActiveTab("create");
  };

  const handleDelete = (offer) => {
    setDeleteModal({
      isOpen: true,
      offer: offer,
    });
  };

  const closeDeleteModal = () => {
    setDeleteModal({
      isOpen: false,
      offer: null,
    });
  };

  const confirmDelete = () => {
    if (deleteModal.offer) {
      deleteOffer(deleteModal.offer._id);
      closeDeleteModal();
    }
  };

  const handleCancel = () => {
    setEditingOffer(null);
    setActiveTab("view");
  };

  const handleSuccess = () => {
    setEditingOffer(null);
    setActiveTab("view");
  };

  if (offersError) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <h2 className="text-red-500 text-xl">
          {offersError || "Failed to load offers"}
        </h2>
      </div>
    );
  }

  return (
    <Dashboard title="Offer Management" description="Create and manage offers">
      {isGettingOffers ? (
        <Loading />
      ) : (
        <>
          <div className="w-full h-full px-4 pb-8">
            {/* Tab Navigation */}
            <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg mb-6">
              <button
                onClick={() => setActiveTab("view")}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  activeTab === "view"
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                View Offers
              </button>
              <button
                onClick={() => setActiveTab("create")}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  activeTab === "create"
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                {editingOffer ? "Edit Offer" : "Create Offer"}
              </button>
            </div>

            {/* Content */}
            {activeTab === "view" ? (
              <div className="bg-white rounded-lg shadow-lg p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-semibold text-gray-900">
                    All Offers ({offers?.length || 0})
                  </h2>
                  <button
                    onClick={() => setActiveTab("create")}
                    className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
                  >
                    Add New Offer
                  </button>
                </div>

                {!offers || offers.length === 0 ? (
                  <div className="text-center py-12">
                    <svg
                      className="mx-auto h-12 w-12 text-gray-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7"
                      />
                    </svg>
                    <h3 className="mt-2 text-sm font-medium text-gray-900">
                      No offers
                    </h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Get started by creating a new offer.
                    </p>
                  </div>
                ) : (
                  <OfferTable
                    offers={offers}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                  />
                )}
              </div>
            ) : (
              <OfferForm
                editingOffer={editingOffer}
                onCancel={editingOffer ? handleCancel : null}
                onSuccess={handleSuccess}
              />
            )}
          </div>

          <DeleteOfferModal
            isOpen={deleteModal.isOpen}
            onClose={closeDeleteModal}
            onConfirm={confirmDelete}
            offerName={deleteModal.offer?.title || ""}
            isDeleting={isDeletingOffer}
          />
        </>
      )}
    </Dashboard>
  );
};

export default Offers;
